import { colors } from '../../styleSheet';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import AlertBox from '../tools/AlertBox';
import { SHOWN_OBJECT_IDS } from './Constants';
import {
  buildAlignedSessionCells,
  NAVIGATION_STATUS,
  prettyName,
  utcToday,
} from './Utils';
import { MonitoringTableRow } from './monitoringTable/MonitoringTableRow';
import { UI } from './tokens';

// Status → dot color, so each navigation-status group is visually coded at a glance.
const STATUS_DOT = {
  finished: colors.success,
  navigating: colors.violetAccent,
  started: colors.violetAccent,
  processing: colors.blueAccent,
  scheduled: colors.grayMuted,
  planned: colors.grayMuted,
  not_started: colors.grayMuted,
  not_planned: colors.grayMuted,
  skipped: colors.grayMuted,
  failed: colors.error,
  aborted: colors.error,
  cancelled: colors.error,
  timed_out: colors.error,
};
const statusDotColor = (status) =>
  STATUS_DOT[String(status).toLowerCase().replace(/\s+/g, '_')] ?? colors.grayMuted;

// Uppercase, tinted sticky header cells — the same fresher table look used by the
// pipeline drawer's Progress tables, applied to the fleet session list.
const headerCellSx = {
  fontWeight: 700,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: UI.textSecondary,
  backgroundColor: UI.surfaceMuted,
  borderBottom: `1px solid ${UI.border}`,
};

// Sort value per column: strings for identity columns, completion ratios for the
// navigation/object progress columns (their bottleneck process), so ordering matches
// what the progress bars show.
function getSortValue(rowInfo, key) {
  const { session, sessionDict } = rowInfo;
  if (key === 'chain') return sessionDict?.chain?.toLowerCase() ?? null;
  if (key === 'store')
    return (sessionDict?.storeName || sessionDict?.store)?.toLowerCase() ?? null;
  if (key === 'session') return session?.toLowerCase() ?? null;
  if (key === 'plannedFor') {
    // plannedFor is a date string (not necessarily ISO), so compare as epoch.
    const time = sessionDict?.plannedFor
      ? new Date(sessionDict.plannedFor).getTime()
      : NaN;
    return Number.isNaN(time) ? null : time;
  }
  if (key === 'navigation') {
    const nav = sessionDict?.navigationData;
    if (!nav?.maxValue) return null;
    return (nav.progressValues?.[0] ?? 0) / nav.maxValue;
  }
  if (key.startsWith('object-')) {
    const objectId = Number(key.slice('object-'.length));
    const objectEntry = sessionDict?.objectData?.find(
      (object) => object.objectId === objectId
    );
    if (!objectEntry?.maxValue || !objectEntry.progressValues?.length)
      return null;
    return Math.min(...objectEntry.progressValues) / objectEntry.maxValue;
  }
  return null;
}

// Header cell with the shared sticky-header styling and a MUI sort toggle.
function SortableHeaderCell({ columnKey, label, sortConfig, onSort, sx = {} }) {
  const active = sortConfig?.key === columnKey;
  return (
    <TableCell
      align="center"
      sortDirection={active ? sortConfig.direction : false}
      sx={{ ...headerCellSx, ...sx }}
    >
      <TableSortLabel
        active={active}
        direction={active ? sortConfig.direction : 'asc'}
        onClick={() => onSort(columnKey)}
        sx={{
          position: 'relative',
          '& .MuiTableSortLabel-icon': {
            position: 'absolute',
            right: -22,
          },
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

export default function PipelineMonitoringTable(props) {
  const {
    alerts,
    setAlertResume = () => {},
    getAlerts,
    getAllMetadata,
    isLoadingSignedBlob,
    loadingAlerts,
    objectNames,
    robot,
    signBlob,
    signedBlob,
    store,
    getSessionProgressTable,
    sessionProgressTableData,
    isSessionProgressTableLoading,
    sessionProgressTableDate,
    filterText = '',
    selectedChains = [],
    showTestSessions = false,
    selectedDate = utcToday(),
    canShowNavigationTitle,
    cardElevation = 3,
    sxCard = {},
  } = props;
  const { t } = useTranslation();

  // Column ordering: one sort applied within every navigation-status group.
  // Defaults to latest planned time first; clicking another column cycles
  // ascending → descending → back to the default, while the planned-time
  // column itself just toggles direction.
  const DEFAULT_SORT = { key: 'plannedFor', direction: 'desc' };
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const handleSort = (key) => {
    setSortConfig((previous) => {
      if (previous?.key !== key) return { key, direction: 'asc' };
      if (previous.direction === 'asc') return { key, direction: 'desc' };
      return key === DEFAULT_SORT.key ? { key, direction: 'asc' } : DEFAULT_SORT;
    });
  };

  // The store tags the table data with the date it was fetched for
  // (sessionProgressTableDate); while it differs from the picked date the view
  // shows the loading block instead of the previous date's rows. The 60s poll
  // re-fetches the same date, so background refreshes never blank the table.
  const isTableDataStale = sessionProgressTableDate !== selectedDate;

  // Handlers
  const handleGetProgressInfo = useCallback(
    (force = false) => {
      // A date change must always fetch, even if a previous request is still
      // in flight — the slice drops out-of-date responses (last request wins).
      if (force || !isSessionProgressTableLoading) {
        getAlerts({ store, robot, metric_type: 'batch_latency' });
        getSessionProgressTable({ store, robot, date: selectedDate });
      }
    },
    [getAlerts, getSessionProgressTable, robot, selectedDate, store]
  );

  const handleSessionsLen = (tableRows) => {
    const tableRowsLen = showTestSessions
      ? tableRows.length
      : tableRows.filter((row) => !row?.sessionDict?.test).length;
    return tableRowsLen;
  };

  // Hooks
  useEffect(() => {
    if (alerts && alerts.length) {
      setAlertResume(alerts);
    }
  }, [alerts, setAlertResume]);

  useEffect(() => {
    getAllMetadata();
  }, [getAllMetadata]);

  const navigationStatusRows = useMemo(() => {
    if (!sessionProgressTableData) {
      return [];
    }

    let filteredRows = Object.entries(sessionProgressTableData).filter(
      ([session, sessionDict]) =>
        (session?.toLowerCase().includes(filterText.toLowerCase()) ||
          sessionDict?.store
            ?.toLowerCase()
            .includes(filterText.toLowerCase()) ||
          sessionDict?.chain
            ?.toLowerCase()
            .includes(filterText.toLowerCase())) &&
        (selectedChains?.includes(sessionDict['chain']) ||
          selectedChains?.length === 0)
    );

    const sessionsAlert =
      alerts?.reduce(
        (beforeObj, alertInfo) => ({
          ...beforeObj,
          [alertInfo?.incident.metric.labels?.session_id]: true,
        }),
        {}
      ) || {};

    filteredRows = filteredRows.sort((session) =>
      sessionsAlert[session[0]] ? -1 : 1
    );

    // Rows with no sortable value always sink to the bottom, in either direction.
    const sortRows = (rows) => {
      if (!sortConfig) return rows;
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      return [...rows].sort((a, b) => {
        const valueA = getSortValue(a, sortConfig.key);
        const valueB = getSortValue(b, sortConfig.key);
        if (valueA == null && valueB == null) return 0;
        if (valueA == null) return 1;
        if (valueB == null) return -1;
        if (valueA < valueB) return -direction;
        if (valueA > valueB) return direction;
        return 0;
      });
    };

    if (store) {
      return filteredRows?.length > 0
        ? [
            [
              filteredRows[0][1]?.['navigationStatus'],
              [
                {
                  session: filteredRows[0][0],
                  sessionDict: filteredRows[0][1],
                },
              ],
            ],
          ]
        : [];
    }

    const groupedRows = filteredRows.reduce(
      (beforeObj, [session, sessionDict]) => {
        const status = sessionDict['navigationStatus'];
        if (beforeObj[status]) {
          beforeObj[status].push({ session, sessionDict });
        } else {
          beforeObj[status] = [{ session, sessionDict }];
        }
        return beforeObj;
      },
      {}
    );

    return Object.entries(NAVIGATION_STATUS).map(
      ([_, navigationStatusName]) => {
        return groupedRows[navigationStatusName]
          ? [navigationStatusName, sortRows(groupedRows[navigationStatusName])]
          : [navigationStatusName, []];
      }
    );
  }, [alerts, filterText, selectedChains, sessionProgressTableData, sortConfig, store]);

  useEffect(() => {
    // This effect handles polling for real-time data updates. The immediate
    // fetch is forced so a date change always requests its own data, even if a
    // previous request is mid-flight; interval ticks stay guarded.
    if (selectedDate >= utcToday()) {
      // For today's date, fetch data immediately and then set up an interval to poll for new data.
      handleGetProgressInfo(true);
      const interval = setInterval(() => handleGetProgressInfo(), 60000); // Poll every 60 seconds for real-time feel

      // The cleanup function will clear the interval when the component unmounts
      // or when dependencies change, preventing memory leaks.
      return () => clearInterval(interval);
    } else {
      // For past dates, just fetch the data once.
      handleGetProgressInfo(true);
    }
  }, [selectedDate, handleGetProgressInfo]);

  return ((isSessionProgressTableLoading || loadingAlerts) &&
    navigationStatusRows.length === 0) ||
    isTableDataStale ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <CircularProgress sx={{ mt: 2 }} />
      <Typography>
        {t('overseer_app.pipeline_monitoring.loading_sessions', 'Loading Sessions...')}
      </Typography>
    </Box>
  ) : (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {navigationStatusRows?.length > 0 ? (
        navigationStatusRows.map(([navigationStatus, tableRows], index) => (
          <Card
            key={`${navigationStatus}-${index}-nav-status-box`}
            elevation={cardElevation}
            sx={{
              mt: 2,
              mb: 2,
              borderRadius: UI.radiusCard,
              background: UI.surface,
              border: `1px solid ${UI.border}`,
              overflow: 'hidden',
              ...sxCard,
            }}
          >
            {canShowNavigationTitle && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${UI.border}`,
                  backgroundColor: UI.surfaceMuted,
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: statusDotColor(navigationStatus),
                    boxShadow: `0 0 0 3px ${statusDotColor(
                      navigationStatus
                    )}22`,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: UI.textPrimary,
                    textTransform: 'capitalize',
                  }}
                >
                  {navigationStatus}
                </Typography>
                <Chip
                  label={`${handleSessionsLen(tableRows)} sessions`}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.textSecondary,
                    backgroundColor: UI.surface,
                    border: `1px solid ${UI.border}`,
                  }}
                />
              </Box>
            )}
            {tableRows?.length > 0 ? (
              <TableContainer sx={{ maxHeight: '90vh' }}>
                <Table stickyHeader sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        key={'chain-head'}
                        columnKey="chain"
                        label="Chain"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableHeaderCell
                        key={'store-head'}
                        columnKey="store"
                        label="Store"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableHeaderCell
                        key={'session-head'}
                        columnKey="session"
                        label="Session"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableHeaderCell
                        key={'planned-head'}
                        columnKey="plannedFor"
                        label="Planned Time"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        sx={{ minWidth: 120 }}
                      />
                      <SortableHeaderCell
                        key={'navigation-head'}
                        columnKey="navigation"
                        label="Navigation"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        sx={{ minWidth: 120 }}
                      />
                      {SHOWN_OBJECT_IDS.map((objectId, index) => (
                        <SortableHeaderCell
                          key={`${objectId}-${index}-table-cell`}
                          columnKey={`object-${objectId}`}
                          label={prettyName(objectNames[objectId])}
                          sortConfig={sortConfig}
                          onSort={handleSort}
                          sx={{ minWidth: 120 }}
                        />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.map((rowInfo, index) => (
                      <MonitoringTableRow
                        key={`${rowInfo.session}-${index}-monitoring-table-row`}
                        chain={rowInfo.sessionDict?.chain}
                        store={rowInfo.sessionDict?.store}
                        storeName={rowInfo.sessionDict?.storeName}
                        session={rowInfo.session}
                        objectData={buildAlignedSessionCells(
                          rowInfo.sessionDict
                        )}
                        selectedDate={selectedDate}
                        test={rowInfo.sessionDict?.test}
                        showTestSessions={showTestSessions}
                        alertInfo={alerts?.find((alertInfo) =>
                          // ! Fix
                          alertInfo.incident.metric.labels !== {}
                            ? alertInfo?.incident.metric.labels?.session_id ===
                              rowInfo.session
                            : null
                        )}
                        navigationReportSentTimestamp={
                          rowInfo.sessionDict?.navigation_report_sent_timestamp
                        }
                        plannedFor={rowInfo.sessionDict?.plannedFor}
                        signBlob={signBlob}
                        signedblob={signedBlob}
                        isLoadingSignedBlob={isLoadingSignedBlob}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : store != null ? (
              <AlertBox
                severity="warning"
                content={t(
                  'overseer_app.robot_view.no_sessions',
                  'No sessions'
                )}
                sx={{ mb: 2 }}
              />
            ) : (
              <Box sx={{ height: '2vh' }}></Box>
            )}
          </Card>
        ))
      ) : (
        <AlertBox
          severity="warning"
          content={t('overseer_app.robot_view.no_sessions', 'No sessions')}
          sx={{ mb: 2 }}
        />
      )}
    </Box>
  );
}
